import {Client} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

// 내부 상태 관리
let stompClient = null;
let currentCompanySeq = null;
let reconnectAttempts = 0;
let maxReconnectAttempts = -1; //-1 : 최대 재연결 시도 횟수 미정
let reconnectDelay = 60000;
let reconnectTimer = null;
let currentCallbacks = {};

// 연결 상태를 동적으로 평가하는 함수
const evaluateConnectionState = (companySeq) => {
    const isNotConnected = !stompClient && !currentCompanySeq;
    const isSameCompanyConnected = stompClient && currentCompanySeq === companySeq && stompClient.connected;
    const isSameCompanyDisconnected = stompClient && currentCompanySeq === companySeq && (!stompClient.connected || stompClient.connecting);
    const isDifferentCompany = stompClient && currentCompanySeq !== companySeq && (stompClient.connected || stompClient.connecting);
    
    return {
        NOT_CONNECTED: isNotConnected,
        SAME_COMPANY_CONNECTED: isSameCompanyConnected,
        SAME_COMPANY_DISCONNECTED: isSameCompanyDisconnected,
        DIFFERENT_COMPANY: isDifferentCompany
    };
};
const createConnection = (companySeq, callbacks = {}, options = {}) => {
    reconnectDelay = options.retryDelay || reconnectDelay || 60000;
    reconnectAttempts = 0;
    currentCallbacks = { ...callbacks };

    const connectionState = evaluateConnectionState(companySeq);

    // 이미 연결된 경우
    if (connectionState.SAME_COMPANY_CONNECTED) {
        return stompClient;
    }
    
    // 같은 회사 + 재연결 필요
    if (connectionState.SAME_COMPANY_DISCONNECTED) {
        cleanUpConnection();
        return createNewConnection(companySeq, callbacks, options);
    }
    
    // 다른 회사 + 재연결 필요
    if (connectionState.DIFFERENT_COMPANY) {
        
        stompClient?.disconnect();
        return createNewConnection(companySeq, callbacks, options);
    }

    // 연결 없음
    if (connectionState.NOT_CONNECTED) {
        return createNewConnection(companySeq, callbacks, options);
    }
    return stompClient;
}

const createNewConnection = (companySeq, callbacks = {}, options = {}) => {
    maxReconnectAttempts = options.maxRetryCount || -1;
    reconnectDelay = options.retryDelay || 3000;
    currentCompanySeq = companySeq;
    reconnectAttempts = 0;
    currentCallbacks = { ...callbacks };

    const wsUrl = process.env.VUE_APP_WS_URL || '/ws-endpoint';
    
    // STOMP 클라이언트 설정 객체 생성
    const clientConfig = {
        webSocketFactory: () => {
            return new SockJS(wsUrl);
        },
        reconnectDelay: reconnectDelay,
        maxReconnectAttempts: maxReconnectAttempts,
        onConnect: () => {
            console.log('STOMP 연결 성공');
            reconnectAttempts = 0; // 연결 성공 시 재연결 시도 횟수 리셋
            if (reconnectTimer) {
                console.log('clearTimeout STOMP reconnectTimer');
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            if (currentCallbacks.onConnect) {
                currentCallbacks.onConnect();
            }
        },
        onUnhandledFrame : (message) => {
            console.log('onUnhandledMessage STOMP', message);
            // if (currentCallbacks.onUnhandledMessage) {
            //     currentCallbacks.onUnhandledMessage(message);
            // }
        },
        onWebSocketClose: () => {
            console.log('onWebSocketClose STOMP 연결 종료');
                currentCallbacks.onDisconnect();
        },
        onDisconnect: () => {
            console.log('onDisconnect STOMP 연결 종료');
            currentCallbacks.onDisconnect();
            attemptReconnect();
        },
        onStompError: (frame) => {
            console.error('onStompError STOMP 에러:', frame);
            if (currentCallbacks.onError) {
                currentCallbacks.onError(frame);
            }
            attemptReconnect();
        },
    };
    
    // 개발 환경에서만 debug 함수 추가 (프로덕션에서는 속성 자체를 추가하지 않음)
    // if (process.env.NODE_ENV === 'development') {
    //     clientConfig.debug = function(str) {
    //         console.log('[STOMP debug]', str);
    //     };
    // }
    
    stompClient = new Client(clientConfig);
    
    try {
        stompClient.activate();
    } catch (error) {
        console.error('STOMP 클라이언트 활성화 실패:', error);
        if (currentCallbacks.onError) {
            currentCallbacks.onError(error);
        }
        attemptReconnect();
    }
}

const cleanUpConnection = () => {
    console.log('cleanUpConnection STOMP');
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    
    currentCompanySeq = null;
    reconnectAttempts = 0;
    currentCallbacks = {};

    if (stompClient) {
        try {
            if (stompClient.connected || stompClient.connecting) {
                stompClient.deactivate();
            }
        } catch (error) {
            console.error('연결 정리 중 오류:', error);
        }
        stompClient = null;
    }
    console.log('cleanUpConnection success');
}
const attemptReconnect = () => {
    if (stompClient && stompClient.connected || maxReconnectAttempts === 0) {
        return;
    }
    if (maxReconnectAttempts > 0 && reconnectAttempts >= maxReconnectAttempts) {
        console.log(`최대 재연결 시도 횟수(${maxReconnectAttempts})에 도달했습니다. 재연결을 중지합니다.`);
        return;
    }
    if (!currentCompanySeq) {
        console.log('재연결할 companySeq가 없습니다.');
        return;
    }   

    // 기존 재연결 타이머가 있으면 정리
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    
    reconnectAttempts++;

    console.log(`재연결 시도 ${reconnectAttempts}${maxReconnectAttempts === -1 ? ' (무한 재시도)' : `/${maxReconnectAttempts}`} - ${reconnectDelay}ms 후 시도`);
    
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        console.log('attemptReconnect success');
        createNewConnection(currentCompanySeq, currentCallbacks, {
            retryDelay: reconnectDelay,
            maxRetryCount: maxReconnectAttempts,
        });
    }, reconnectDelay);
}

const getStompClient = () => {
    return stompClient;
}

const disconnect = () => {
    if (stompClient) {
        cleanUpConnection();
    }
}

const isConnected = () => {
    return stompClient ? stompClient.connected : false;
}

const getStompConnectionState = () => {
    return stompClient ? stompClient.getConnectionState() : null;
}

const stompManager = {
    createConnection,
    disconnect,
    isConnected,
    getConnectionState: getStompConnectionState,
    cleanUpConnection,
    createNewConnection,
    attemptReconnect,
    getStompClient,
  };

export default stompManager;